// REACH-S1 (docs/design/2026-10-28-agent-reach.md §2, slice R1): the jsonb shapes of an agent's
// Reach and of a template version's reporting defaults. Zod only (no Drizzle), safe for web.
//
// An audience is who a Reach field names: the whole company, or named people, seats and teams (a
// team includes its sub-teams). The supervisor chain is never stored: every check adds it (a
// person above an agent always reaches and sees it, the reporting-chain rule).
import { z } from "zod";
import { Uuid } from "./primitives";

const ids = (max: number) => z.array(Uuid).max(max);

export const ReachAudience = z
  .strictObject({
    /** Everyone in the company (the named lists are then moot). */
    company: z.boolean(),
    /** Person memberships. */
    people: ids(200),
    /** Seats: whoever holds them now. */
    seats: ids(200),
    /** Teams, with their sub-teams: whoever holds a seat in them. */
    teams: ids(100),
  })
  .meta({ id: "ReachAudience" });
export type ReachAudience = z.infer<typeof ReachAudience>;

/** Whom an agent serves: a person, a seat, a team or the company (`id` null). */
export const ReachServes = z
  .strictObject({
    kind: z.enum(["person", "seat", "team", "company"]),
    id: Uuid.nullable(),
  })
  .refine((s) => (s.kind === "company") === (s.id === null), {
    message: "the company has no id; a person, seat or team needs one",
    path: ["id"],
  })
  .meta({ id: "ReachServes" });
export type ReachServes = z.infer<typeof ReachServes>;

/** The five fields of a Reach (an edit names the ones it changes). */
export const ReachField = z
  .enum(["serves", "hears_from", "talks_to", "extra_spaces", "visible_to"])
  .meta({ id: "ReachField" });
export type ReachField = z.infer<typeof ReachField>;

/** A Reach as stored and audited (`agent.reach_changed` before / after). */
export const ReachSnapshot = z
  .strictObject({
    serves: ReachServes,
    hears_from: ReachAudience,
    talks_to: ReachAudience,
    extra_spaces: z.array(Uuid).max(50),
    visible_to: ReachAudience,
    /** False: the agent ran on its defaults (no row yet). */
    stored: z.boolean(),
  })
  .meta({ id: "ReachSnapshot" });
export type ReachSnapshot = z.infer<typeof ReachSnapshot>;

/** Nobody beyond the chain. */
export const EMPTY_AUDIENCE: ReachAudience = { company: false, people: [], seats: [], teams: [] };

/**
 * A template version's reporting defaults (§2 "every field has a default from the template's
 * `reporting_defaults` and the seat"). Each key absent means the platform default: Serves the
 * seat's team (else the company; `supervisor`: the supervisor seat); Hears from and Talks to the
 * serving scope; Who sees the chain and the serving person.
 */
export const ReportingDefaults = z
  .strictObject({
    serves: z.enum(["team", "supervisor", "company"]).optional(),
    hears_from: z.enum(["serving", "company"]).optional(),
    talks_to: z.enum(["serving", "company"]).optional(),
    visible_to: z.enum(["chain", "serving", "company"]).optional(),
  })
  .meta({ id: "ReportingDefaults" });
export type ReportingDefaults = z.infer<typeof ReportingDefaults>;
